import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Share, Facebook, Twitter, Linkedin, ChevronRight, Tag, MessageSquare } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { useToast } from '../hooks/useToast';
import { supabase } from '../utils/supabaseClient';
import { trackEvent } from '../utils/posthog';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author_id: string;
  category: string;
  tags: string[];
  image_url: string | null;
  status: string;
  published_at: string | null;
  created_at: string;
  author_name?: string;
  author_bio?: string;
}

interface Comment {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  user_name?: string;
}

const BlogPost: React.FC = () => {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);

  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page: 'blog_post',
      post_slug: slug
    });
    
    const fetchPost = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        
        // Fetch the blog post
        const { data: postData, error: postError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .eq('status', 'published')
          .single();
          
        if (postError) throw postError;
        
        if (!postData) {
          navigate('/blog');
          return;
        }
        
        // Fetch author information
        let authorName = 'Editorial Team';
        let authorBio = 'The 11th Admissions editorial team consists of education experts and counselors with extensive experience in the field.';
        
        if (postData.author_id) {
          try {
            // Try to get user info from public users table
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('name')
              .eq('id', postData.author_id)
              .maybeSingle();
              
            if (!userError && userData) {
              authorName = userData.name;
            }
          } catch (error) {
            console.error('Error fetching author data:', error);
          }
        }
        
        // Fetch comments
        const { data: commentsData, error: commentsError } = await supabase
          .from('blog_comments')
          .select('*')
          .eq('post_id', postData.id)
          .order('created_at', { ascending: false });
          
        if (!commentsError) {
          // Fetch user names for comments
          const commentsWithUserNames = await Promise.all(
            (commentsData || []).map(async (comment) => {
              if (comment.user_id) {
                try {
                  const { data: userData, error: userError } = await supabase
                    .from('users')
                    .select('name')
                    .eq('id', comment.user_id)
                    .maybeSingle();
                    
                  if (!userError && userData) {
                    return {
                      ...comment,
                      user_name: userData.name
                    };
                  }
                } catch (error) {
                  console.error('Error fetching comment user data:', error);
                }
              }
              
              return {
                ...comment,
                user_name: 'Anonymous User'
              };
            })
          );
          
          setComments(commentsWithUserNames);
        }
        
        // Fetch related posts (same category)
        const { data: relatedData, error: relatedError } = await supabase
          .from('blog_posts')
          .select('id, title, slug, category')
          .eq('category', postData.category)
          .eq('status', 'published')
          .neq('id', postData.id)
          .limit(2);
          
        if (!relatedError) {
          setRelatedPosts(relatedData || []);
        }
        
        // Set the post with author info
        setPost({
          ...postData,
          author_name: authorName,
          author_bio: authorBio
        });
      } catch (error) {
        console.error('Error fetching blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [slug, navigate]);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post?.title || '11th Admissions Blog';
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank');
        break;
      default:
        navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard!', 'success');
    }
    
    // Track share event
    trackEvent('blog_post_share', {
      platform,
      post_slug: slug,
      post_title: post?.title
    });
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !post?.id) return;
    
    // Check if user is logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      showToast('Please log in to comment', 'error', {
        label: 'Log In',
        onClick: () => navigate('/login')
      });
      return;
    }
    
    try {
      setCommentLoading(true);
      
      // Add comment to database
      const { data: commentData, error: commentError } = await supabase
        .from('blog_comments')
        .insert({
          post_id: post.id,
          user_id: session.user.id,
          content: commentText
        })
        .select('*, users:user_id(name)')
        .single();
        
      if (commentError) throw commentError;
      
      // Add new comment to state
      setComments([
        {
          ...commentData,
          user_name: commentData.users?.name || 'User'
        },
        ...comments
      ]);
      
      // Clear comment input
      setCommentText('');
      
      // Track comment event
      trackEvent('blog_comment_submitted', {
        post_slug: slug,
        post_title: post?.title
      });
      
      showToast('Comment added successfully', 'success');
    } catch (error) {
      console.error('Error submitting comment:', error);
      showToast('Failed to submit comment', 'error');
    } finally {
      setCommentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
                <div className="h-64 bg-gray-200 rounded w-full mb-8"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6 mb-6"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Blog Post Not Found</h1>
              <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been moved.</p>
              <Link 
                to="/blog" 
                className="inline-flex items-center px-4 py-2 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumbs */}
          <div className="max-w-4xl mx-auto mb-6">
            <div className="flex items-center text-sm text-gray-500">
              <Link to="/" className="hover:text-[#7B4AE2]">Home</Link>
              <ChevronRight className="h-4 w-4 mx-2" />
              <Link to="/blog" className="hover:text-[#7B4AE2]">Blog</Link>
              <ChevronRight className="h-4 w-4 mx-2" />
              <Link to={`/blog/category/${post.category.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-[#7B4AE2]">
                {post.category}
              </Link>
              <ChevronRight className="h-4 w-4 mx-2" />
              <span className="text-gray-700 truncate">{post.title}</span>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <div className="bg-white rounded-t-lg shadow-sm p-6 mb-1">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
              
              <div className="flex flex-wrap items-center text-sm text-gray-600 mb-6">
                <span className="flex items-center mr-4 mb-2">
                  <User className="h-4 w-4 mr-1.5" />
                  {post.author_name}
                </span>
                <span className="flex items-center mr-4 mb-2">
                  <Calendar className="h-4 w-4 mr-1.5" />
                  {new Date(post.published_at || post.created_at).toLocaleDateString()}
                </span>
                <span className="flex items-center mr-4 mb-2">
                  <Clock className="h-4 w-4 mr-1.5" />
                  {Math.ceil(post.content.length / 1000)} min read
                </span>
                <span className="flex items-center mb-2">
                  <Tag className="h-4 w-4 mr-1.5" />
                  {post.category}
                </span>
              </div>
              
              <div className="rounded-lg overflow-hidden mb-6">
                <img 
                  src={post.image_url || 'https://images.pexels.com/photos/256541/pexels-photo-256541.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'} 
                  alt={post.title} 
                  className="w-full h-auto object-cover"
                />
              </div>
              
              <p className="text-lg text-gray-700 italic">{post.excerpt}</p>
            </div>
            
            {/* Article Content */}
            <div className="bg-white shadow-sm p-6 prose max-w-none">
              <div dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br />') }} />
            </div>
            
            {/* Article Footer */}
            <div className="bg-white rounded-b-lg shadow-sm p-6 border-t">
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <Link 
                        key={index} 
                        to={`/blog/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#F0E9FF] text-[#7B4AE2] hover:bg-[#E0D9FF]"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Share */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Share this article:</h3>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleShare('facebook')}
                    className="p-2 rounded-full bg-[#F0E9FF] text-[#7B4AE2] hover:bg-[#E0D9FF]"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => handleShare('twitter')}
                    className="p-2 rounded-full bg-[#F0E9FF] text-[#7B4AE2] hover:bg-[#E0D9FF]"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => handleShare('linkedin')}
                    className="p-2 rounded-full bg-[#F0E9FF] text-[#7B4AE2] hover:bg-[#E0D9FF]"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => handleShare('copy')}
                    className="p-2 rounded-full bg-[#F0E9FF] text-[#7B4AE2] hover:bg-[#E0D9FF]"
                    aria-label="Copy link"
                  >
                    <Share className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Author Bio */}
              <div className="border-t border-b border-gray-100 py-6 mb-6">
                <h3 className="text-lg font-semibold mb-2">About the Author</h3>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-12 w-12 rounded-full bg-[#F0E9FF] flex items-center justify-center text-[#7B4AE2] font-bold text-lg">
                      {post.author_name?.split(' ').map(n => n[0]).join('')}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">{post.author_name}</h4>
                    <p className="text-sm text-gray-600">{post.author_bio}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Comments Section */}
            <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-semibold mb-6">Comments</h3>
              
              <form onSubmit={handleCommentSubmit} className="mb-8">
                <div className="mb-4">
                  <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                    Leave a comment
                  </label>
                  <textarea
                    id="comment"
                    rows={4}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#7B4AE2] focus:border-[#7B4AE2]"
                    placeholder="Share your thoughts..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={commentLoading || !commentText.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7B4AE2] hover:bg-[#6A3ACF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B4AE2] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {commentLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Posting...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-4 w-4 mr-1.5" />
                      Post Comment
                    </>
                  )}
                </button>
              </form>
              
              <div className="space-y-6">
                {comments.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No comments yet. Be the first to comment!</p>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-[#F0E9FF] flex items-center justify-center text-[#7B4AE2] font-medium">
                          {comment.user_name?.charAt(0) || 'U'}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium">{comment.user_name}</h4>
                          <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-6">Related Articles</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {relatedPosts.map((related, index) => (
                    <Link 
                      key={index} 
                      to={`/blog/${related.slug}`}
                      className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                      onClick={() => trackEvent('related_post_click', { 
                        from_post: slug,
                        to_post: related.slug
                      })}
                    >
                      <div className="p-5">
                        <h4 className="font-bold text-gray-900 mb-2">{related.title}</h4>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-1.5" />
                          <span>Read more</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            {/* Back to Blog */}
            <div className="mt-8 text-center">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-[#7B4AE2] hover:text-[#6A3ACF]"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPost;